import { useQueryClient } from "@tanstack/react-query";
import { Table } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomDateTabs,
  CustomPopup,
  CustomTable,
  Filter,
  FilterTag,
} from "../../../../../components";

import { moneyFormat } from "../../../../../plugins/numberFormat";
import { envChangeType, getCurrency } from "../../../../../plugins/utils";
import { filterKeyUserData } from "../../enumerations/filterKeyEnum";
import { filterSetupUserData } from "../../enumerations/filterSetupEnum";
import {
  useGetSubordinateUserData,
  useGetSubordinateUserDetailData,
} from "../../hooks/subordinate.hooks";
import styles from "./style.module.scss";
const UserData = () => {
  const nowCurrency = getCurrency();
  const { t } = useTranslation();
  const cache = useQueryClient();

  const [filterDefaultValue, setFilterDefaultValue] = useState({
    [filterKeyUserData.ACCOUNT]: "",
    [filterKeyUserData.ACCOUNT_ID]: "",
    [filterKeyUserData.SETTLED_START_DATE]: dayjs().format("YYYY-MM-DD 00:00:00"),
    [filterKeyUserData.SETTLED_END_DATE]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });
  const [filterValue, setFilterValue] = useState({
    [filterKeyUserData.ACCOUNT]: "",
    [filterKeyUserData.ACCOUNT_ID]: "",
    [filterKeyUserData.SETTLED_START_DATE]: dayjs().format("YYYY-MM-DD 00:00:00"),
    [filterKeyUserData.SETTLED_END_DATE]: dayjs().format("YYYY-MM-DD 23:59:59"),
  });

  const [localData, setLocalData] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openDetailDrawer, setOpenDetailDrawer] = useState(false);
  const [detailTitle, setDetailTitle] = useState("");
  const [summary, setSummary] = useState({
    totalBetAmount: 0,
    totalWinLoss: 0,
  });

  const onOpenDetail = (id, account_name) => {
    setSelectedRowId(id);
    setOpenDetailDrawer(true);
    setDetailTitle(account_name);
  };

  const resetDrawer = () => {
    setDetailTitle("");
    setSelectedRowId(null);
  };

  const userDataColumns = [
    {
      title: t("normal.account"),
      width: 130,
      dataIndex: "account",
      key: "account",
      align: "center",
    },
    {
      title: t("normal.uid"),
      width: 90,
      dataIndex: "account_id",
      key: "account_id",
      align: "center",
    },
    {
      title: t("mobile.subordinate.userData.deposit", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_deposit_amount",
      key: "sum_deposit_amount",
      width: 100,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.withdraw", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      currency: t(`global.currency.${nowCurrency}`),
      dataIndex: "sum_withdrawal_amount",
      key: "sum_withdrawal_amount",
      width: 100,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.bonus", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_promotion_rebate_amount",
      key: "sum_promotion_rebate_amount",
      width: 100,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.bet", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_bet_amount",
      key: "sum_bet_amount",
      width: 120,
      align: "center",
      render: (text, record, index) => {
        return <>{moneyFormat(text)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.winlost", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_win_loss",
      key: "sum_win_loss",
      width: 100,
      fixed: "right",
      align: "center",
      render: (text, record, index) => {
        return (
          <div
            onClick={() => onOpenDetail(record.account_id, record.account)}
            className={`${parseFloat(text) > 0 ? styles.textGreen : styles.textRed} ${
              styles.underlined
            }`}
          >
            {moneyFormat(text)}
          </div>
        );
      },
    },
  ];

  const userDetailDataColumns = [
    {
      title: t("subordinate.userData.gameCategory"),
      dataIndex: "game_category_code",
      align: "center",
      width: 120,
      render: (text, record, index) => {
        return `${t([
          `comission.platform.game.${envChangeType("REACT_APP_PRODUCTION_NAME_DEV")}.${record.game_3th_code}`,
          `comission.platform.game.${record.game_3th_code}`,
        ])}`;
      },
    },
    {
      title: t("mobile.subordinate.userData.churn", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_bet_amount",
      align: "center",
      width: 120,
      render: (text, record, index) => {
        return <>{moneyFormat(record.sum_bet_amount)}</>;
      },
    },
    {
      title: t("mobile.subordinate.userData.winlost", {
        currency: t(`global.currency.${nowCurrency}`),
      }),
      dataIndex: "sum_win_loss",
      align: "center",
      width: 120,
      render: (text, record, index) =>
        typeof record.sum_win_loss == "number" ? (
          <span className={`${parseFloat(text) > 0 ? styles.textGreen : styles.textRed}`}>
            {moneyFormat(record.sum_win_loss)}
          </span>
        ) : (
          <>{record.sum_win_loss}</>
        ),
    },
  ];
  const {
    data: userData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
  } = useGetSubordinateUserData({
    params: {
      ...filterValue,
    },
    options: {
      getNextPageParam: (lastPage, pages) => {
        if (pages.length < lastPage.last_page) {
          return pages.length + 1;
        } else {
          return undefined;
        }
      },
    },
  });
  const {
    data: userDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useGetSubordinateUserDetailData({
    params: {
      ...filterValue,
    },
    childId: selectedRowId,
    options: {
      enabled: selectedRowId !== null,
    },
  });

  const loadMore = () => {
    if (isFetchingNextPage === false && localData.length > 0) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (userData) {
      const page_length = userData.pages.length;
      if (page_length <= 1) {
        setLocalData([...userData.pages[0].data]);
      } else {
        setLocalData((prevState) => {
          return [...prevState, ...userData.pages[page_length - 1].data];
        });
      }
    }
  }, [userData]);

  const onHandleSearch = () => {};

  useEffect(() => {
    if (userDetail) {
      let totalBetAmount = 0;
      let totalWinLoss = 0;
      userDetail.data.forEach((data) => {
        totalBetAmount += data.sum_bet_amount;
        totalWinLoss += data.sum_win_loss;
      });
      setSummary({
        totalBetAmount: totalBetAmount,
        totalWinLoss: totalWinLoss,
      });
    }
  }, [userDetail]);

  return (
    <div>
      <Filter
        filterSetup={filterSetupUserData}
        filterDefaultValue={filterDefaultValue}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        onReset={() => {
          //if need some aditional reset logic in the page
          console.debug("RESET FILTER");
        }}
        onSubmitSearch={onHandleSearch}
      />
      <CustomDateTabs
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        startDateKey={filterKeyUserData.START_DATE}
        endDateKey={filterKeyUserData.END_DATE}
      />
      <FilterTag
        filterSetup={filterSetupUserData}
        filterValue={filterValue}
        filterDefaultValue={filterDefaultValue}
        setFilterValue={setFilterValue}
        refreshSearch={onHandleSearch}
      />
      {isLoading ? (
        ""
      ) : isError ? (
        ""
      ) : (
        <CustomTable
          columns={userDataColumns}
          dataSource={localData}
          hasMore={hasNextPage}
          loadMore={loadMore}
          showInifineScroll={true}
        />
      )}
      <CustomPopup
        visible={openDetailDrawer}
        onClose={() => {
          setOpenDetailDrawer(false);
        }}
        afterClose={resetDrawer}
        title={detailTitle}
      >
        {isDetailLoading ? (
          ""
        ) : isDetailError ? (
          ""
        ) : (
          <CustomTable
            columns={userDetailDataColumns}
            dataSource={userDetail.data}
            showInifineScroll={false}
            summary={() => {
              return (
                <Table.Summary fixed="top">
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <div className={styles.summaryCell}>{t("normal.summary")}</div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <div className={`${styles.summaryCell} ${styles.content}`}>
                        {moneyFormat(summary.totalBetAmount || 0)}
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <div
                        className={`${styles.summaryCell} ${styles.content} ${
                          summary.totalWinLoss <= 0 ? styles.redColor : styles.greenColor
                        }`}
                      >
                        {moneyFormat(summary.totalWinLoss || 0)}
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        )}
      </CustomPopup>
    </div>
  );
};

export default UserData;
